import { alpha } from "@mui/material/styles";
import { chipSemantico } from "../../../utils/centralDeAlertas/cores";
import { fontWeight } from "@mui/system";

// Estilos da linha do tempo de um incidente (linha expandida da TabelaIncidentes):
// trilha horizontal no desktop e vertical abaixo de "md".

const styles = (theme) => ({
	container: {
		display: "flex",
		flexDirection: "column",
		gap: 2,
		p: "16px",
		width: "100%",
	},
	resumo: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "flex-start",
		gap: 2,
		flexWrap: "wrap",
	},
	resumoTextos: {
		display: "flex",
		flexDirection: "column",
		gap: "2px",
		minWidth: 0,
	},
	resumoTitulo: {
		fontWeight: 600,
		fontSize: "14px",
		color: theme.palette.text.primary,
	},
	resumoDescricao: {
		color: theme.palette.text.secondary,
		fontWeight: 700
	},
	linkServico: {
		fontWeight: 600,
	},
	chipStatus: (cor) => ({
		...chipSemantico(theme, cor),
		border: "1px solid",
		fontWeight: 600,
		height: "24px",
		"& .MuiChip-icon": {
			color: cor,
			fontSize: "16px",
		},
	}),
	trilha: {
		display: "flex",
		flexDirection: "row",
		alignItems: "flex-start",
		width: "100%",
		[theme.breakpoints.down("md")]: {
			flexDirection: "column",
			alignItems: "stretch",
		},
	},
	marco: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		textAlign: "center",
		gap: "6px",
		flexShrink: 0,
		maxWidth: "160px",
		[theme.breakpoints.down("md")]: {
			flexDirection: "row",
			alignItems: "flex-start",
			textAlign: "left",
			gap: "12px",
			maxWidth: "none",
		},
	},
	// Círculo do marco: cheio (cor semântica) quando registrado, tracejado
	// e esmaecido quando ausente.
	marcoCirculo: (cor, presente) => ({
		width: "36px",
		height: "36px",
		minWidth: "36px",
		borderRadius: "50%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		border: presente
			? `2px solid ${alpha(cor, 0.5)}`
			: `2px dashed ${theme.palette.divider}`,
		backgroundColor: presente
			? alpha(cor, theme.palette.mode === "dark" ? 0.16 : 0.1)
			: "transparent",
		color: presente ? cor : theme.palette.text.secondary,
		"& svg": {
			fontSize: "20px",
			opacity: presente ? 1 : 0.4,
		},
	}),
	// Conteúdo do tooltip dos marcos com lista (atendimentos/OS da janela):
	// uma linha por registro, título em negrito.
	tooltipConteudo: {
		display: "flex",
		flexDirection: "column",
		gap: "2px",
		p: "2px",
		maxWidth: "360px",
	},
	tooltipTitulo: {
		fontWeight: 600,
		fontSize: "12px",
		lineHeight: 1.4,
	},
	tooltipLinha: {
		fontSize: "12px",
		lineHeight: 1.4,
		whiteSpace: "normal",
	},
	// Tooltip rico do cluster de incidentes, no padrão do "Atendimentos
	// Excedidos" (fluxoAtendimentoEtapaComponent): cabeçalho + lista rolável
	// de mini-cards, um por alerta. Dentro do Tooltip do MUI o texto herda a
	// cor clara do balão — a hierarquia é por opacidade, não text.secondary.
	tooltipCluster: {
		maxWidth: 320,
	},
	tooltipCabecalhoCluster: {
		p: 1,
		pb: 0.5,
		display: "flex",
		flexDirection: "column",
		gap: "2px",
	},
	tooltipCaption: {
		fontSize: "11px",
		lineHeight: 1.4,
		opacity: 0.75,
	},
	tooltipListaCluster: {
		display: "flex",
		flexDirection: "column",
		gap: 1,
		maxHeight: "260px",
		overflow: "auto",
		p: 1,
	},
	// Card de um alerta do cluster sobre o balão escuro do Tooltip.
	paperAlertaCluster: {
		backgroundColor: alpha(theme.palette.common.black, 0.25),
		borderColor: alpha(theme.palette.common.white, 0.2),
		color: "inherit",
		p: 1,
		display: "flex",
		flexDirection: "column",
		gap: 0.5,
	},
	linhaSeveridadeCluster: {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		gap: "6px",
	},
	pontoSeveridade: (cor) => ({
		width: "8px",
		height: "8px",
		minWidth: "8px",
		borderRadius: "50%",
		backgroundColor: cor,
	}),
	rotuloSeveridadeCluster: (cor) => ({
		fontSize: "11px",
		fontWeight: 600,
		color: cor,
	}),
	// Nome completo do alerta, quebrando linha quando preciso — títulos de
	// trigger carregam nomes longos de porta/host sem espaços (word-break).
	tituloAlertaCluster: {
		fontWeight: "bold",
		fontSize: "12px",
		whiteSpace: "normal",
		wordBreak: "break-word",
		lineHeight: 1.4,
	},
	boxTemposCluster: {
		mt: 0.5,
		pt: 0.5,
		borderTop: "1px dashed",
		borderColor: alpha(theme.palette.common.white, 0.3),
		display: "flex",
		flexDirection: "column",
		gap: 0.25,
	},
	tempoCluster: {
		fontSize: "11px",
		fontWeight: 500
	},
	valorTempoCluster: {
		fontWeight: "bold",
	},
	valorRecuperadoCluster: {
		fontWeight: "bold",
		color: theme.palette.success.light,
	},
	valorAbertoCluster: {
		fontWeight: "bold",
		color: theme.palette.warning.main,
	},
	tooltipDica: {
		fontSize: "11px",
		lineHeight: 1.4,
		fontStyle: "italic",
		opacity: 0.7,
		p: 1,
		pt: 0,
	},
	marcoTextos: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		gap: "2px",
		[theme.breakpoints.down("md")]: {
			alignItems: "flex-start",
		},
	},
	marcoRotulo: (presente) => ({
		fontWeight: 600,
		fontSize: "13px",
		lineHeight: 1.2,
		color: presente ? theme.palette.text.primary : theme.palette.text.secondary,
	}),
	marcoHora: {
		color: theme.palette.text.secondary,
		lineHeight: 1.2,
	},
	marcoDetalhe: {
		color: theme.palette.text.secondary,
		lineHeight: 1.2,
	},
	marcoAusencia: {
		color: theme.palette.text.secondary,
		fontStyle: "italic",
		lineHeight: 1.2,
	},
	// Área do conector entre marcos: linha + duração desde o marco anterior
	// registrado. No mobile vira linha vertical alinhada ao centro do círculo.
	conectorArea: {
		flex: "1 1 auto",
		minWidth: "32px",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		gap: "4px",
		pt: "17px",
		px: "4px",
		[theme.breakpoints.down("md")]: {
			flex: "0 0 auto",
			flexDirection: "row",
			alignItems: "center",
			gap: "8px",
			minWidth: 0,
			pt: 0,
			px: 0,
			ml: "17px",
			my: "4px",
		},
	},
	conectorLinha: (tracejado) => ({
		width: "100%",
		height: "2px",
		borderRadius: "1px",
		backgroundColor: tracejado ? "transparent" : theme.palette.divider,
		backgroundImage: tracejado
			? `repeating-linear-gradient(90deg, ${theme.palette.divider} 0px, ${theme.palette.divider} 5px, transparent 5px, transparent 10px)`
			: "none",
		[theme.breakpoints.down("md")]: {
			width: "2px",
			height: "28px",
			backgroundImage: tracejado
				? `repeating-linear-gradient(180deg, ${theme.palette.divider} 0px, ${theme.palette.divider} 5px, transparent 5px, transparent 10px)`
				: "none",
		},
	}),
	conectorDuracao: {
		color: theme.palette.text.secondary,
		whiteSpace: "nowrap",
		lineHeight: 1,
	},
	alertasCategorias: {
		display: "flex",
		flexDirection: "column",
		gap: 1,
	},
	alertaCategoria: (cor) => ({
		borderColor: alpha(cor, 0.5),
		color: theme.palette.text.primary,
		alignItems: "flex-start",
		"& .MuiAlert-icon": {
			color: cor,
		},
	}),
	alertaTitulo: {
		fontWeight: 600,
		fontSize: "13px",
		mb: "2px",
	},
	vazio: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: "100%",
		p: "24px",
	},
});

export default styles;
